import { useEffect, useMemo, useState } from 'react';
import { SearchResponse } from 'instantsearch.js/es/types/algoliasearch';
import { algolia } from '@yi/core';

import { notifyBugsnag, algolia as algoliaCommonCore } from '~/common';

import { useAlgoliaInsights } from './useAlgoliaInsights';

const { getAlgoliaClient } = algoliaCommonCore;
type Mutable<T> = { -readonly [P in keyof T]: T[P] };

type MultipleQueriesType = {
  indexName: string;
  query: string;
  params: {
    hitsPerPage: number;
    page: number;
    enablePersonalization: boolean;
    userToken: string;
    filters?: string;
    facets?: string[];
    facetFilters?: string[][];
    optionalFilters?: string[][];
    sumOrFiltersScores?: boolean;
  };
};

interface Props {
  indexes: Array<
    algolia.AlgoliaIndexesType & {
      params?: { filters?: string; distinct?: false | true | 1 | 2 | 3 | 4 };
    }
  >;
  page?: number;
  hitsPerPage?: number;
  enablePersonalization?: boolean;
  locale: string;
}
interface StateObject<T> {
  header: string;
  items: Array<T>;
}

export interface UseAlgoliaSearchClientReturnType<T> {
  loading: boolean;
  hits: StateObject<T>[];
}

const getQueriesAndOrderedFilters = ({
  indexes,
  hitsPerPage = 12,
  locale,
  page = 0,
  algoliaToken,
  enablePersonalization = true,
}: Props & { algoliaToken: string }) => {
  const queries: MultipleQueriesType[] = [];
  const orderedFilters: string[] = [];
  indexes.forEach(
    ({
      indexName: indexName_,
      filters,
      facetFilters,
      facets,
      optionalFilters,
      sumOrFiltersScores = false,
      params = {},
    }) => {
      const indexName = indexName_.replace('locale', locale);
      const optionalFilters_ = optionalFilters ? optionalFilters : [];
      if (!facetFilters) {
        queries.push({
          indexName,
          query: '',
          params: {
            hitsPerPage,
            page,
            enablePersonalization,
            filters: filters || '',
            facets,
            optionalFilters: optionalFilters_,
            sumOrFiltersScores,
            // Previously, we returned null when missing algoliaToken
            // The Segment ID we use for anonymous users' algoliaToken is unreliable, and can create
            // a race condition, so we need to ensure the carousels can load without this token
            userToken: enablePersonalization && algoliaToken ? algoliaToken : '',
            ...(params.distinct && { distinct: params.distinct }),
          },
        });
      } else {
        Object.entries(facetFilters).forEach(([facetType, value]) => {
          if (value) {
            (locale === 'en' ? value.en : value.es).forEach(facetFilter => {
              if (facetFilter) {
                queries.push({
                  indexName,
                  query: '',
                  params: {
                    hitsPerPage,
                    page,
                    enablePersonalization,
                    userToken: enablePersonalization ? algoliaToken : '',
                    filters: filters || '',
                    facets,
                    facetFilters: [[`${facetType}:${facetFilter}`]],
                    optionalFilters: optionalFilters_,
                    sumOrFiltersScores,
                    ...(params.distinct && { distinct: params.distinct }),
                  },
                });
                orderedFilters.push(facetFilter);
              }
            });
          }
        });
      }
    },
  );
  return { queries, orderedFilters };
};

const algoliaSearchClient = getAlgoliaClient();
export const useAlgoliaSearchClient = <T>({
  indexes,
  enablePersonalization = true,
  locale,
  hitsPerPage = 12,
  page = 0,
}: Props): UseAlgoliaSearchClientReturnType<T> => {
  const { algoliaToken } = useAlgoliaInsights();
  const [loading, setLoading] = useState(true);
  const [hits, setHits] = useState<StateObject<T>[]>([]);

  useEffect(() => {
    setHits([]);
    setLoading(true);
    const { queries, orderedFilters } = getQueriesAndOrderedFilters({
      indexes,
      locale,
      hitsPerPage,
      page,
      enablePersonalization,
      algoliaToken,
    });
    if (queries) {
      algoliaSearchClient
        .multipleQueries<T>(queries)
        .then(res => {
          const resIndex: StateObject<T>[] = [];
          res.results.forEach((result, index) => {
            // @TSFixMe after upgrade to TS 5, result cause errors
            const { hits: hits_ } = result as SearchResponse<any>;
            const selectedOrderedFilter = orderedFilters[index];
            if (orderedFilters.length && selectedOrderedFilter) {
              resIndex.push({
                header: selectedOrderedFilter,
                items: hits_ as Mutable<typeof hits_>,
              });
            } else {
              resIndex.push({ header: '', items: hits_ as Mutable<typeof hits_> });
            }
          });
          setLoading(false);
          setHits(resIndex);
        })
        .catch(error => {
          setLoading(false);
          setHits([]);
          notifyBugsnag(error);
        });
    }
  }, [algoliaToken, page, indexes, hitsPerPage, enablePersonalization, locale]);
  const result = useMemo(() => ({ loading, hits }), [hits, loading]);
  return result;
};
